/**
 * First-party attribution capture (Module 18 foundation).
 *
 * Captures UTM parameters, ad click identifiers, referrer, landing page and
 * coarse device type on first visit and sends visit/page-view events through
 * the rate-limited public.track_marketing_event RPC.
 *
 * - No third-party pixels are loaded (TikTok / Snap / Meta / Google pixels and
 *   server-side conversion APIs are integration points only — REQUIRES API).
 * - No GPS / precise location is collected.
 * - Conversions are recorded server-side, never from the browser.
 * - Consent handling REQUIRES BUSINESS / LEGAL CONFIRMATION: analytics_consent
 *   is sent as false until a consent experience is approved.
 */

export const SESSION_KEY_STORAGE = 'aqs.attribution.session';
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
export const CLICK_ID_KEYS = ['gclid', 'fbclid', 'ttclid', 'ScCid', 'msclkid', 'twclid'] as const;

export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'unknown';

export interface AttributionContext {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  click_ids?: Partial<Record<(typeof CLICK_ID_KEYS)[number], string>>;
  referrer?: string;
  landing_page?: string;
  page_path?: string;
  device_type: DeviceType;
  language: 'ar' | 'en';
  analytics_consent: boolean;
}

/** 24 random URL-safe characters (matches the database session_key format). */
export function generateSessionKey(random: (bytes: Uint8Array) => Uint8Array = (b) => crypto.getRandomValues(b)): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  const bytes = random(new Uint8Array(24));
  let out = '';
  for (const byte of bytes) out += alphabet[byte % 64];
  return out;
}

export function isValidSessionKey(value: string | null | undefined): value is string {
  return !!value && /^[A-Za-z0-9_-]{16,64}$/.test(value);
}

export function detectDeviceType(userAgent: string, viewportWidth?: number): DeviceType {
  const ua = userAgent.toLowerCase();
  if (/ipad|tablet|(android(?!.*mobile))/.test(ua)) return 'tablet';
  if (/mobi|iphone|android/.test(ua)) return 'mobile';
  if (viewportWidth !== undefined) {
    if (viewportWidth < 768) return 'mobile';
    if (viewportWidth < 1024) return 'tablet';
  }
  return ua ? 'desktop' : 'unknown';
}

const clean = (value: string | null, max: number) => {
  const v = (value ?? '').trim().slice(0, max);
  return v || undefined;
};

/** Strips query strings from referrers so no third-party personal data is stored. */
export function sanitizeReferrer(referrer: string, ownOrigin: string): string | undefined {
  if (!referrer) return undefined;
  try {
    const url = new URL(referrer);
    if (url.origin === ownOrigin) return undefined; // internal navigation is not a referrer
    return `${url.origin}${url.pathname}`.slice(0, 500);
  } catch {
    return undefined;
  }
}

export function buildAttributionContext(input: {
  url: URL;
  referrer: string;
  userAgent: string;
  viewportWidth?: number;
  language: 'ar' | 'en';
}): AttributionContext {
  const { url } = input;
  const ctx: AttributionContext = {
    device_type: detectDeviceType(input.userAgent, input.viewportWidth),
    language: input.language,
    analytics_consent: false,
    landing_page: url.pathname.slice(0, 500),
    page_path: url.pathname.slice(0, 300),
  };
  for (const key of UTM_KEYS) {
    const v = clean(url.searchParams.get(key), 100);
    if (v) ctx[key] = v;
  }
  const clickIds: AttributionContext['click_ids'] = {};
  for (const key of CLICK_ID_KEYS) {
    const v = clean(url.searchParams.get(key), 200);
    if (v) clickIds[key] = v;
  }
  if (Object.keys(clickIds).length) ctx.click_ids = clickIds;
  const referrer = sanitizeReferrer(input.referrer, url.origin);
  if (referrer) ctx.referrer = referrer;
  return ctx;
}
